// Simple in-memory data cache service
class DataCache {
  constructor() {
    this.cache = new Map();
    this.loadingStates = new Map();
  }

  // Get data from cache
  get(key) {
    const cached = this.cache.get(key);
    return cached ? cached.data : null;
  }

  // Set data in cache
  set(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  // Check if data exists in cache
  has(key) {
    return this.cache.has(key);
  }

  // Clear specific cache entry
  clear(key) {
    this.cache.delete(key);
    this.loadingStates.delete(key);
  }

  // Clear all cache
  clearAll() {
    this.cache.clear();
    this.loadingStates.clear();
  }

  // Set loading state
  setLoading(key, isLoading) {
    this.loadingStates.set(key, isLoading);
  }

  // Get loading state
  isLoading(key) {
    return this.loadingStates.get(key) || false;
  }

  // Fetch data with caching
  async fetchWithCache(key, fetchFunction, defaultData = null) {
    // Return cached data if available
    if (this.has(key)) {
      return this.get(key);
    }

    // Don't fetch if already loading
    if (this.isLoading(key)) {
      return defaultData;
    }

    try {
      this.setLoading(key, true);
      const data = await fetchFunction();
      this.set(key, data);
      return data;
    } catch (error) {
      console.error(`Error fetching ${key}:`, error);
      if (defaultData) {
        this.set(key, defaultData);
        return defaultData;
      }
      throw error;
    } finally {
      this.setLoading(key, false);
    }
  }
}

// Create singleton instance
export const dataCache = new DataCache();

// Helper functions for common operations
export const getCachedData = (key) => dataCache.get(key);
export const setCachedData = (key, data) => dataCache.set(key, data);
export const hasCachedData = (key) => dataCache.has(key);
export const clearCachedData = (key) => dataCache.clear(key);
export const isDataLoading = (key) => dataCache.isLoading(key);
